import { computed, type ComputedRef } from "vue";

export interface ImageProps {
  imageSrc?: string;
}

export interface VideoProps {
  videoSrc?: string;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
}

export function useMediaContent(
  mediaType: ComputedRef<"image" | "video" | undefined>,
  imageProps?: ComputedRef<ImageProps | undefined>,
  videoProps?: ComputedRef<VideoProps | undefined>
) {
        // Определяем источник изображения
        const imageSrc = computed(() => {
          return imageProps?.value?.imageSrc;
        });

  // Определяем источник видео
  const videoSrc = computed(() => {
    const src = videoProps?.value?.videoSrc;

    if (!src) return undefined;

    // Проверяем, является ли это base64 данными
    if (src.startsWith("data:")) {
      return src;
    }

    // Проверяем, является ли это blob URL
    if (src.startsWith("blob:")) {
      return src;
    }

    // Для обычных URL добавляем controls=0
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set("controls", "0");
      return url.toString();
    } catch (error) {
      return src;
    }
  });

  // Определяем, это видео или изображение
  // Приоритет: явно указанный mediaType > автоопределение по данным
  const isVideo = computed(() => {
    // Если mediaType явно указан, используем его
    if (mediaType.value === "video") return true;
    if (mediaType.value === "image") return false;
    
    // Автоопределение: только если mediaType не указан
    // Проверяем, что есть videoSrc И нет imageSrc
    const hasVideoSrc = !!videoSrc.value;
    const hasImageSrc = !!imageSrc.value;
    return hasVideoSrc && !hasImageSrc;
  });
  
  const isImage = computed(() => {
    // Если mediaType явно указан, используем его
    if (mediaType.value === "image") return true;
    if (mediaType.value === "video") return false;
    
    // Автоопределение: только если mediaType не указан
    // Проверяем, что есть imageSrc И нет videoSrc
    const hasImageSrc = !!imageSrc.value;
    const hasVideoSrc = !!videoSrc.value;
    return hasImageSrc && !hasVideoSrc;
  });

  return {
    isVideo,
    isImage,
    videoSrc,
    imageSrc,
  };
}

